import { applyI18n, t } from '../i18n';
import {
  blockRemoteImages,
  isOutlookMsgMagic,
  parseEmail,
  prepareEmailHtml,
  type ParsedEmail,
} from '../files/emailParse';
import { parseMsg } from '../files/msgParse';
import { resolveFilePreview } from '../files/fileTypes';
import { renderArchiveListing, type ArchiveEntry } from './archivePreview';

export async function renderEmailPreview(content: HTMLElement, blob: Blob): Promise<boolean> {
  try {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const email = isOutlookMsgMagic(bytes) ? await parseMsg(bytes) : parseEmail(bytes);
    renderEmail(content, email);
    return true;
  } catch {
    return false;
  }
}

function renderEmail(content: HTMLElement, email: ParsedEmail): void {
  const wrap = document.createElement('div');
  wrap.className = 'file-preview-email';

  const headers = document.createElement('dl');
  headers.className = 'file-preview-email-headers';
  for (const key of ['from', 'to', 'cc', 'subject', 'date']) {
    const value = email.headers[key];
    if (!value) continue;
    const dt = document.createElement('dt');
    dt.textContent = t(`files.preview.email.${key}`);
    const dd = document.createElement('dd');
    dd.textContent = value;
    headers.append(dt, dd);
  }
  wrap.append(headers);

  let preparedHtml: string | null = null;
  let displayAttachments = email.attachments;
  let remoteBlocked = true;
  let blockedCount = 0;
  let iframe: HTMLIFrameElement | null = null;
  let banner: HTMLDivElement | null = null;
  let bannerText: HTMLSpanElement | null = null;
  let bannerButton: HTMLButtonElement | null = null;

  const updateIframeContent = (): void => {
    if (!preparedHtml || !iframe) return;
    const result = blockRemoteImages(preparedHtml, remoteBlocked);
    iframe.srcdoc = result.html;
    if (remoteBlocked) {
      blockedCount = result.blockedCount;
    }
  };

  const updateBanner = (): void => {
    if (!banner || !bannerText || !bannerButton) return;
    if (remoteBlocked) {
      bannerText.dataset.i18n = 'files.preview.email.blockedImages';
      bannerButton.dataset.i18n = 'files.preview.email.showImages';
    } else {
      bannerText.dataset.i18n = 'files.preview.email.shownImages';
      bannerButton.dataset.i18n = 'files.preview.email.hideImages';
    }
    applyI18n(banner);
  };

  if (email.bodyHtml) {
    const prepared = prepareEmailHtml(email.bodyHtml, email.attachments);
    if (prepared) {
      preparedHtml = prepared.html;
      displayAttachments = prepared.attachments;
      const initial = blockRemoteImages(preparedHtml, true);
      blockedCount = initial.blockedCount;

      if (blockedCount > 0) {
        banner = document.createElement('div');
        banner.className = 'file-preview-email-remote-banner';
        bannerText = document.createElement('span');
        bannerText.className = 'file-preview-email-remote-banner__text';
        bannerButton = document.createElement('button');
        bannerButton.type = 'button';
        bannerButton.className = 'button subtle';
        bannerButton.addEventListener('click', () => {
          remoteBlocked = !remoteBlocked;
          updateIframeContent();
          updateBanner();
        });
        banner.append(bannerText, bannerButton);
        wrap.append(banner);
        updateBanner();
      }
    }
  }

  const bodyWrap = document.createElement('div');
  bodyWrap.className = 'file-preview-email-body';

  if (preparedHtml) {
    iframe = document.createElement('iframe');
    iframe.className = 'file-preview-document';
    iframe.sandbox = 'allow-popups';
    iframe.referrerPolicy = 'no-referrer';
    iframe.srcdoc = blockRemoteImages(preparedHtml, remoteBlocked).html;
    bodyWrap.append(iframe);
  } else if (email.bodyText) {
    const pre = document.createElement('pre');
    pre.className = 'file-preview-text';
    pre.textContent = email.bodyText.slice(0, 50_000);
    bodyWrap.append(pre);
  } else {
    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.dataset.i18n = 'files.preview.email.emptyBody';
    bodyWrap.append(empty);
  }
  wrap.append(bodyWrap);

  if (displayAttachments.length > 0) {
    const attachmentsWrap = document.createElement('div');
    attachmentsWrap.className = 'file-preview-email-attachments';
    const heading = document.createElement('h3');
    heading.className = 'file-preview-email-attachments-heading';
    heading.dataset.i18n = 'files.preview.email.attachments';
    attachmentsWrap.append(heading);

    const archiveEntries: ArchiveEntry[] = displayAttachments.map((attachment) => {
      const header = attachment.bytes.subarray(0, Math.min(attachment.bytes.length, 512));
      return {
        name: attachment.fileName,
        size: attachment.bytes.byteLength,
        resolved: resolveFilePreview(attachment.fileName, attachment.contentType, header),
        getBytes: async () => attachment.bytes,
      };
    });
    renderArchiveListing(attachmentsWrap, archiveEntries, { embedded: true });
    wrap.append(attachmentsWrap);
  }

  content.append(wrap);
  applyI18n(wrap);
}
